import React, { useState, useEffect } from "react";
import { Building2, Landmark, Tags, Plus, Trash2, Save, CheckCircle, Pencil, X } from "lucide-react";
import { api } from "../../utils/api";

export default function Settings() {
  const [activeTab, setActiveTab] = useState("general");
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [isEditingGeneral, setIsEditingGeneral] = useState(false); // Değiştir modu kontrolü

  // 1. GENEL BİLGİLER VE BANKALAR (Veritabanındaki 'settings' tablosunda birlikte tutulur)
  const [settingsData, setSettingsData] = useState({
    site_name: "Başkent Sitesi",
    tax_office: "",
    tax_number: "",
    address: "",
    late_rate: 5,
    distribution: "Eşit Dağıtım",
    currency: "TL",
    is_professional: false,
    manager1: "",
    manager2: "",
    manager3: "",
    banks: []
  });

  // 2. BANKA EKLEME / DÜZENLEME STATE'LERİ
  const [bankForm, setBankForm] = useState({ name: "", account_holder: "", branch_code: "", iban: "" });
  const [editingBank, setEditingBank] = useState(null);

  // 3. GİDER KALEMLERİ STATE'LERİ (typesofexpenses tablosu)
  const [categories, setCategories] = useState([]);
  const [newCatCode, setNewCatCode] = useState("");
  const [newCatName, setNewCatName] = useState("");
  const [editingCat, setEditingCat] = useState(null);

  // Veritabanından Ayarları Çek
  const loadSettings = () => {
    api.get("/settings").then(r => {
      if (r.data) {
        setSettingsData(prev => ({
          ...prev,
          ...r.data,
          site_name: r.data.site_name || r.data.siteName || "Başkent Sitesi",
          banks: Array.isArray(r.data.banks) ? r.data.banks : []
        }));
      }
    }).catch(err => console.error("Ayarlar çekilemedi:", err));
  };

  // Veritabanından Gider Türlerini Çek
  const loadCategories = () => {
    api.get("/typesofexpenses").then(r => {
      if (r.data && Array.isArray(r.data)) setCategories(r.data);
    }).catch(err => console.error("Gider türleri çekilemedi:", err));
  };

  useEffect(() => {
    loadSettings();
    loadCategories();
  }, []);

  const showSaved = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  // GENEL BİLGİLERİ VERİTABANINA KAYDET
  const handleGeneralSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...settingsData,
        siteName: settingsData.site_name // Makbuz vb. yerlerle tam uyumluluk için
      };
      await api.put("/settings", payload);
      setSettingsData(payload);
      setIsEditingGeneral(false);
      showSaved();
    } catch (err) {
      alert("Ayarlar veritabanına kaydedilemedi!");
    } finally {
      setLoading(false);
    }
  };

  // BANKA EKLE VEYA GÜNCELLE (settings tablosunun içine kaydeder)
  const saveBank = async () => {
    if (!bankForm.name.trim()) {
      alert("Lütfen en azından Banka Adını girin.");
      return;
    }

    let updatedBanks = [...(settingsData.banks || [])];

    if (editingBank) {
      updatedBanks = updatedBanks.map(b => b.id === editingBank.id ? { ...bankForm, id: editingBank.id } : b);
    } else {
      updatedBanks.push({ ...bankForm, id: Date.now().toString() });
    }

    const updatedSettings = { ...settingsData, banks: updatedBanks };

    try {
      await api.put("/settings", updatedSettings);
      setSettingsData(updatedSettings);
      setBankForm({ name: "", account_holder: "", branch_code: "", iban: "" });
      setEditingBank(null);
      showSaved();
    } catch (err) {
      alert("Banka bilgisi veritabanına kaydedilemedi!");
    }
  };

  const startEditBank = (bank) => {
    setEditingBank(bank);
    setBankForm({
      name: bank.name || "",
      account_holder: bank.account_holder || "",
      branch_code: bank.branch_code || "",
      iban: bank.iban || ""
    });
  };

  const cancelEditBank = () => {
    setEditingBank(null);
    setBankForm({ name: "", account_holder: "", branch_code: "", iban: "" });
  };

  const removeBank = async (id) => {
    if (!window.confirm("Bu banka hesabını silmek istediğinize emin misiniz?")) return;
    const updatedBanks = (settingsData.banks || []).filter(b => b.id !== id);
    const updatedSettings = { ...settingsData, banks: updatedBanks };
    try {
      await api.put("/settings", updatedSettings);
      setSettingsData(updatedSettings);
      showSaved();
    } catch (err) {
      alert("Banka silinemedi!");
    }
  };

  // GİDER KATEGORİSİ EKLE VEYA GÜNCELLE
  const saveCategory = async () => {
    if (!newCatName || !newCatCode) {
      alert("Lütfen hem muhasebe kodunu hem de gider adını girin.");
      return;
    }
    const payload = { code: newCatCode, name: newCatName };
    try {
      if (editingCat) {
        const id = editingCat.id || editingCat._id;
        await api.put(`/typesofexpenses/${id}`, payload);
      } else {
        await api.post("/typesofexpenses", payload);
      }
      setNewCatCode("");
      setNewCatName("");
      setEditingCat(null);
      loadCategories();
      showSaved();
    } catch (err) {
      alert("İşlem veritabanına kaydedilemedi.");
    }
  };

  const startEditCategory = (cat) => {
    setEditingCat(cat);
    setNewCatCode(cat.code);
    setNewCatName(cat.name);
  };

  const cancelEditCategory = () => {
    setEditingCat(null);
    setNewCatCode("");
    setNewCatName("");
  };

  const removeCategory = async (cat) => {
    if (!window.confirm(`"${cat.code} - ${cat.name}" gider kalemini silmek istediğinize emin misiniz?`)) return;
    const id = cat.id || cat._id;
    try {
      await api.delete(`/typesofexpenses/${id}`);
      loadCategories();
      showSaved();
    } catch (err) {
      alert("Silme işlemi başarısız oldu.");
    }
  };

  const inputStyle = (disabled) => ({
    width: "100%",
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    outline: "none",
    fontSize: "14px",
    background: disabled ? "#f1f5f9" : "white",
    color: disabled ? "#475569" : "#1e293b",
    cursor: disabled ? "not-allowed" : "text"
  });

  return (
    <div style={{ animation: "fadeIn 0.4s ease-out" }}>
      
      {/* ÜST BAŞLIK */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "white", padding: "24px 30px", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px rgba(0,0,0,0.02)", marginBottom: "24px" }}>
        <div>
          <span style={{ fontSize: "12px", fontWeight: "bold", color: "#64748b", letterSpacing: "1px", textTransform: "uppercase" }}>SİSTEM YAPILANDIRMASI</span>
          <h2 style={{ margin: "6px 0", fontSize: "22px", color: "#1e293b" }}>Ayarlar ve Tanımlamalar</h2>
          <p style={{ margin: 0, fontSize: "14px", color: "#64748b" }}>Site bilgilerini, yöneticileri, banka hesaplarını ve muhasebe gider kalemlerini buradan yönetin.</p>
        </div>
        {saved && (
          <div style={{ display: "flex", alignItems: "center", gap: "8px", background: "#ecfdf5", color: "#059669", padding: "10px 16px", borderRadius: "10px", fontWeight: "bold" }}>
            <CheckCircle size={20} /> Veritabanına Kaydedildi
          </div>
        )}
      </div>

      {/* SEKMELER */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "24px", flexWrap: "wrap" }}>
        <button onClick={() => setActiveTab("general")} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "12px 20px", borderRadius: "12px", fontWeight: "600", cursor: "pointer", border: "none", transition: "0.2s", background: activeTab === "general" ? "#3b82f6" : "white", color: activeTab === "general" ? "white" : "#64748b", boxShadow: activeTab === "general" ? "0 4px 12px rgba(59,130,246,0.3)" : "0 2px 4px rgba(0,0,0,0.02)" }}>
          <Building2 size={18} /> Genel Bilgiler
        </button>
        <button onClick={() => setActiveTab("banks")} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "12px 20px", borderRadius: "12px", fontWeight: "600", cursor: "pointer", border: "none", transition: "0.2s", background: activeTab === "banks" ? "#10b981" : "white", color: activeTab === "banks" ? "white" : "#64748b", boxShadow: activeTab === "banks" ? "0 4px 12px rgba(16,185,129,0.3)" : "0 2px 4px rgba(0,0,0,0.02)" }}>
          <Landmark size={18} /> Banka Hesapları ({settingsData.banks?.length || 0})
        </button>
        <button onClick={() => setActiveTab("categories")} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "12px 20px", borderRadius: "12px", fontWeight: "600", cursor: "pointer", border: "none", transition: "0.2s", background: activeTab === "categories" ? "#f59e0b" : "white", color: activeTab === "categories" ? "white" : "#64748b", boxShadow: activeTab === "categories" ? "0 4px 12px rgba(245,158,11,0.3)" : "0 2px 4px rgba(0,0,0,0.02)" }}>
          <Tags size={18} /> Gider Kalemleri ve Kodları ({categories.length})
        </button>
      </div>

      {/* ================================================================= */}
      {/* 1. SEKME: GENEL BİLGİLER VE YÖNETİCİLER                           */}
      {/* ================================================================= */}
      {activeTab === "general" && (
        <div style={{ background: "white", borderRadius: "16px", padding: "30px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px rgba(0,0,0,0.02)" }}>
          
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #f1f5f9", paddingBottom: "16px", marginBottom: "24px" }}>
            <div>
              <h3 style={{ margin: 0, color: "#1e293b", fontSize: "18px" }}>Site / Apartman Kimlik ve Yönetim Bilgileri</h3>
              <span style={{ fontSize: "13px", color: "#64748b" }}>Veritabanındaki <b>settings</b> tablosunda kayıtlı resmi bilgilerdir.</span>
            </div>
            {!isEditingGeneral ? (
              <button type="button" onClick={() => setIsEditingGeneral(true)} style={{ background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe", padding: "10px 20px", borderRadius: "8px", fontWeight: "bold", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}>
                <Pencil size={16} /> Bilgileri Değiştir
              </button>
            ) : (
              <button type="button" onClick={() => { setIsEditingGeneral(false); loadSettings(); }} style={{ background: "#f1f5f9", color: "#475569", border: "1px solid #cbd5e1", padding: "10px 20px", borderRadius: "8px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}>
                <X size={16} /> İptal
              </button>
            )}
          </div>

          <form onSubmit={handleGeneralSave}>
            {/* SİTE TEMEL BİLGİLERİ (ESKİ TABLO VERİLERİ DÂHİL) */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "20px", marginBottom: "20px" }}>
              <div>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>Site / Apartman Adı *</label>
                <input type="text" disabled={!isEditingGeneral} value={settingsData.site_name} onChange={e => setSettingsData({ ...settingsData, site_name: e.target.value })} style={inputStyle(!isEditingGeneral)} required />
              </div>
              <div>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>Vergi Dairesi</label>
                <input type="text" disabled={!isEditingGeneral} value={settingsData.tax_office || ""} onChange={e => setSettingsData({ ...settingsData, tax_office: e.target.value })} placeholder="Örn: Çankaya V.D." style={inputStyle(!isEditingGeneral)} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>Vergi Numarası</label>
                <input type="text" disabled={!isEditingGeneral} value={settingsData.tax_number || ""} onChange={e => setSettingsData({ ...settingsData, tax_number: e.target.value })} placeholder="10 Haneli Vergi No" style={inputStyle(!isEditingGeneral)} />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "20px", marginBottom: "20px" }}>
              <div>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>Gecikme Tazminatı Oranı (%)</label>
                <input type="number" step="0.1" disabled={!isEditingGeneral} value={settingsData.late_rate ?? 5} onChange={e => setSettingsData({ ...settingsData, late_rate: Number(e.target.value) })} style={inputStyle(!isEditingGeneral)} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>Gider Dağıtım Şekli</label>
                <select disabled={!isEditingGeneral} value={settingsData.distribution || "Eşit Dağıtım"} onChange={e => setSettingsData({ ...settingsData, distribution: e.target.value })} style={inputStyle(!isEditingGeneral)}>
                  <option value="Eşit Dağıtım">Eşit Dağıtım</option>
                  <option value="Arsa Payına Göre">Arsa Payına Göre</option>
                </select>
              </div>
              <div>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>Para Birimi</label>
                <input type="text" disabled={!isEditingGeneral} value={settingsData.currency || "TL"} onChange={e => setSettingsData({ ...settingsData, currency: e.target.value })} style={inputStyle(!isEditingGeneral)} />
              </div>
            </div>

            <div style={{ marginBottom: "24px" }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>Açık Adres</label>
              <textarea rows="2" disabled={!isEditingGeneral} value={settingsData.address || ""} onChange={e => setSettingsData({ ...settingsData, address: e.target.value })} placeholder="Site / Apartman açık adresi..." style={{ ...inputStyle(!isEditingGeneral), fontFamily: "inherit" }} />
            </div>

            {/* YÖNETİM KURULU (3 YÖNETİCİ) */}
            <div style={{ background: "#f8fafc", padding: "20px", borderRadius: "12px", border: "1px solid #e2e8f0", marginBottom: "24px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "10px" }}>
                <h4 style={{ margin: 0, color: "#1e293b", fontSize: "15px" }}>Yönetim Kurulu / Yöneticiler</h4>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: isEditingGeneral ? "pointer" : "not-allowed", fontSize: "13px", fontWeight: "600", color: "#475569" }}>
                  <input type="checkbox" disabled={!isEditingGeneral} checked={settingsData.is_professional || false} onChange={e => setSettingsData({ ...settingsData, is_professional: e.target.checked })} style={{ width: "16px", height: "16px" }} />
                  Dışarıdan Profesyonel Yönetim Şirketi İle Çalışılıyor
                </label>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#1e293b", marginBottom: "6px" }}>
                    Yönetici 1 (Başkan / Makbuz İmza Yetkilisi)
                  </label>
                  <input type="text" disabled={!isEditingGeneral} value={settingsData.manager1 || ""} onChange={e => setSettingsData({ ...settingsData, manager1: e.target.value })} placeholder="Ad Soyad" style={inputStyle(!isEditingGeneral)} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>
                    Yönetici 2 (Başkan Yrd. / Üye)
                  </label>
                  <input type="text" disabled={!isEditingGeneral} value={settingsData.manager2 || ""} onChange={e => setSettingsData({ ...settingsData, manager2: e.target.value })} placeholder="Ad Soyad" style={inputStyle(!isEditingGeneral)} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>
                    Yönetici 3 (Muhasip / Denetçi)
                  </label>
                  <input type="text" disabled={!isEditingGeneral} value={settingsData.manager3 || ""} onChange={e => setSettingsData({ ...settingsData, manager3: e.target.value })} placeholder="Ad Soyad" style={inputStyle(!isEditingGeneral)} />
                </div>
              </div>
            </div>

            {isEditingGeneral && (
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                <button type="submit" disabled={loading} style={{ background: "#3b82f6", color: "white", padding: "12px 28px", borderRadius: "8px", fontWeight: "bold", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", boxShadow: "0 4px 12px rgba(59,130,246,0.25)" }}>
                  <Save size={18} /> {loading ? "Kaydediliyor..." : "Değişiklikleri Kaydet"}
                </button>
              </div>
            )}
          </form>
        </div>
      )}

      {/* ================================================================= */}
      {/* 2. SEKME: BANKA HESAPLARI (Ad, Hesap Sahibi, Şube Kodu, IBAN)     */}
      {/* ================================================================= */}
      {activeTab === "banks" && (
        <div style={{ background: "white", borderRadius: "16px", padding: "30px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px rgba(0,0,0,0.02)" }}>
          <h3 style={{ marginTop: 0, color: "#1e293b", borderBottom: "1px solid #f1f5f9", paddingBottom: "12px", marginBottom: "20px" }}>Kayıtlı Banka Hesapları</h3>
          
          <div style={{ display: "grid", gap: "12px", marginBottom: "30px" }}>
            {(settingsData.banks || []).map(bank => (
              <div key={bank.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: editingBank?.id === bank.id ? "#ecfdf5" : "#f8fafc", padding: "16px 20px", borderRadius: "12px", border: editingBank?.id === bank.id ? "1px solid #6ee7b7" : "1px solid #e2e8f0", flexWrap: "wrap", gap: "16px" }}>
                <div style={{ display: "grid", gap: "4px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <strong style={{ fontSize: "16px", color: "#1e293b" }}>{bank.name}</strong>
                    {bank.branch_code && (
                      <span style={{ background: "#e2e8f0", color: "#475569", padding: "2px 8px", borderRadius: "6px", fontSize: "12px", fontWeight: "600" }}>
                        Şube Kodu: {bank.branch_code}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: "13px", color: "#475569" }}>
                    <strong>Hesap Sahibi:</strong> {bank.account_holder || "Belirtilmemiş"}
                  </div>
                  <div style={{ fontSize: "14px", color: "#059669", fontFamily: "monospace", fontWeight: "bold" }}>
                    {bank.iban || "IBAN Girilmemiş"}
                  </div>
                </div>
                
                <div style={{ display: "flex", gap: "8px" }}>
                  <button onClick={() => startEditBank(bank)} title="Değiştir" style={{ background: "white", color: "#3b82f6", border: "1px solid #bfdbfe", padding: "8px 12px", borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", fontWeight: "600", fontSize: "13px" }}>
                    <Pencil size={15} /> Değiştir
                  </button>
                  <button onClick={() => removeBank(bank.id)} title="Sil" style={{ background: "#fef2f2", color: "#ef4444", border: "1px solid #fecaca", padding: "8px 12px", borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", fontWeight: "600", fontSize: "13px" }}>
                    <Trash2 size={15} /> Sil
                  </button>
                </div>
              </div>
            ))}
            {(!settingsData.banks || settingsData.banks.length === 0) && (
              <div style={{ color: "#94a3b8", fontStyle: "italic", padding: "20px", textAlign: "center", background: "#f8fafc", borderRadius: "12px" }}>
                Henüz kayıtlı bir banka hesabı bulunmuyor. Aşağıdaki formdan ekleyebilirsiniz.
              </div>
            )}
          </div>

          <h4 style={{ color: editingBank ? "#059669" : "#475569", marginBottom: "12px", display: "flex", alignItems: "center", gap: "8px" }}>
            {editingBank ? <><Pencil size={18} /> Banka Hesabını Güncelle</> : "Yeni Banka Hesabı Ekle"}
          </h4>
          
          <div style={{ background: editingBank ? "#f0fdf4" : "#f8fafc", padding: "20px", borderRadius: "12px", border: editingBank ? "1px solid #bbf7d0" : "1px solid #e2e8f0" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px", marginBottom: "16px" }}>
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>Banka Adı *</label>
                <input type="text" placeholder="Örn: Ziraat Bankası" value={bankForm.name} onChange={e => setBankForm({ ...bankForm, name: e.target.value })} style={inputStyle(false)} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>Hesap Sahibi</label>
                <input type="text" placeholder="Örn: Başkent Sitesi Yönetimi" value={bankForm.account_holder} onChange={e => setBankForm({ ...bankForm, account_holder: e.target.value })} style={inputStyle(false)} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>Şube Kodu / Adı</label>
                <input type="text" placeholder="Örn: 1234 - Çayyolu Şb." value={bankForm.branch_code} onChange={e => setBankForm({ ...bankForm, branch_code: e.target.value })} style={inputStyle(false)} />
              </div>
            </div>

            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "flex-end" }}>
              <div style={{ flex: 1, minWidth: "260px" }}>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#475569", marginBottom: "6px" }}>IBAN Numarası</label>
                <input type="text" placeholder="TR00 0000 0000 0000 0000 0000 00" value={bankForm.iban} onChange={e => setBankForm({ ...bankForm, iban: e.target.value })} style={{ ...inputStyle(false), fontFamily: "monospace" }} />
              </div>

              {editingBank && (
                <button onClick={cancelEditBank} style={{ background: "white", color: "#475569", padding: "11px 20px", borderRadius: "8px", fontWeight: "600", border: "1px solid #cbd5e1", cursor: "pointer" }}>
                  Vazgeç
                </button>
              )}
              <button onClick={saveBank} style={{ background: "#10b981", color: "white", padding: "11px 24px", borderRadius: "8px", fontWeight: "bold", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}>
                {editingBank ? <Save size={18} /> : <Plus size={18} />}
                {editingBank ? "Bankayı Güncelle" : "Banka Ekle"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* 3. SEKME: GİDER KALEMLERİ VE KODLARI                              */}
      {/* ================================================================= */}
      {activeTab === "categories" && (
        <div style={{ background: "white", borderRadius: "16px", padding: "30px", border: "1px solid #e2e8f0", boxShadow: "0 4px 6px rgba(0,0,0,0.02)" }}>
          
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #f1f5f9", paddingBottom: "12px", marginBottom: "20px" }}>
            <h3 style={{ margin: 0, color: "#1e293b" }}>Muhasebe Kodları ve Gider Türleri</h3>
            <span style={{ background: "#fef3c7", color: "#b45309", padding: "4px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: "bold" }}>
              {categories.length} Kayıtlı Gider
            </span>
          </div>
          
          <div style={{ display: "grid", gap: "10px", marginBottom: "30px", maxHeight: "380px", overflowY: "auto", paddingRight: "8px" }}>
            {categories.map((cat) => (
              <div key={cat.id || cat._id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: editingCat && (editingCat.id === cat.id) ? "#fef3c7" : "#f8fafc", padding: "12px 20px", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ display: "inline-block", width: "75px", background: "white", padding: "4px 8px", borderRadius: "6px", fontSize: "13px", color: "#b45309", border: "1px solid #fcd34d", marginRight: "16px", fontWeight: "bold", textAlign: "center" }}>
                    {cat.code}
                  </span>
                  <span style={{ fontSize: "15px", color: "#1e293b", fontWeight: "600" }}>{cat.name}</span>
                </div>
                
                <div style={{ display: "flex", gap: "8px" }}>
                  <button onClick={() => startEditCategory(cat)} title="Değiştir" style={{ background: "white", color: "#3b82f6", border: "1px solid #bfdbfe", padding: "8px", borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center" }}>
                    <Pencil size={16} />
                  </button>
                  <button onClick={() => removeCategory(cat)} title="Sil" style={{ background: "#fef2f2", color: "#ef4444", border: "1px solid #fecaca", padding: "8px", borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center" }}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <h4 style={{ color: editingCat ? "#d97706" : "#475569", marginBottom: "12px", display: "flex", alignItems: "center", gap: "8px" }}>
            {editingCat ? <><Pencil size={18} /> Gider Kalemini Düzenle</> : "Yeni Gider Ekle"}
          </h4>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", background: editingCat ? "#fffbeb" : "#f8fafc", padding: "16px", borderRadius: "12px", border: editingCat ? "1px solid #fde68a" : "1px solid #e2e8f0" }}>
            <input type="text" placeholder="Muhasebe Kodu (Örn: 740.03)" value={newCatCode} onChange={e => setNewCatCode(e.target.value)} style={{ width: "180px", padding: "12px", borderRadius: "8px", border: "1px solid #cbd5e1", outline: "none", fontWeight: "bold", color: "#b45309" }} />
            <input type="text" placeholder="Gider Adı (Örn: Site Personel Gideri)" value={newCatName} onChange={e => setNewCatName(e.target.value)} onKeyDown={e => e.key === "Enter" && saveCategory()} style={{ flex: 1, minWidth: "200px", padding: "12px", borderRadius: "8px", border: "1px solid #cbd5e1", outline: "none" }} />
            {editingCat && (
              <button onClick={cancelEditCategory} style={{ background: "white", color: "#475569", padding: "12px 20px", borderRadius: "8px", fontWeight: "600", border: "1px solid #cbd5e1", cursor: "pointer" }}>
                Vazgeç
              </button>
            )}
            <button onClick={saveCategory} style={{ background: editingCat ? "#f59e0b" : "#10b981", color: "white", padding: "12px 24px", borderRadius: "8px", fontWeight: "bold", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}>
              {editingCat ? <Save size={18} /> : <Plus size={18} />} 
              {editingCat ? "Güncelle" : "Ekle"}
            </button>
          </div>
        </div>
      )}

    </div>
  );
}